import { create } from 'zustand'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

export const ADMIN_EMAIL_DOMAIN = '@admin.xifanacg.com'

export interface AdminClaims {
  sub: string
  email: string
  username: string
  /** OIDC `picture` claim (Supabase copies it into user_metadata); null if absent / not http(s). */
  picture: string | null
  is_admin: boolean
  is_moderator: boolean
  exp: number
  /** Top-level JWT claim; permanent VIP overrides timed expiry. */
  vip_is_permanent: boolean
  /** ISO-8601 expiry for timed VIP; null when permanent / unset / malformed. */
  vip_expires_at: string | null
}

export interface VipStatus {
  isPermanentVip: boolean
  isTimedVip: boolean
  isVip: boolean
}

function pickUsername(
  meta: Record<string, unknown>,
  email: string,
): string {
  const candidates = ['username', 'nickname', 'name', 'full_name', 'preferred_username']
  for (const key of candidates) {
    const v = meta[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  const prefix = email.split('@')[0]
  return prefix || 'user'
}

function pickPicture(
  meta: Record<string, unknown>,
  topLevel: unknown,
): string | null {
  for (const v of [meta.picture, topLevel, meta.avatar_url]) {
    if (typeof v === 'string' && /^https?:\/\//i.test(v.trim())) return v.trim()
  }
  return null
}

interface AuthState {
  session: Session | null
  claims: AdminClaims | null
  ready: boolean
  setSession: (session: Session | null) => void
  markReady: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  claims: null,
  ready: false,
  setSession: (session) =>
    set({
      session,
      claims: session ? parseClaims(session.access_token) : null,
    }),
  markReady: () => set({ ready: true }),
}))

export function parseClaims(accessToken: string): AdminClaims | null {
  try {
    const payload = accessToken.split('.')[1]
    if (!payload) return null
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
    const json = JSON.parse(atob(padded)) as Record<string, unknown>
    const email = String(json.email ?? '')
    const userMeta =
      (json.user_metadata as Record<string, unknown> | undefined) ?? {}
    return {
      sub: String(json.sub ?? ''),
      email,
      username: pickUsername(userMeta, email),
      picture: pickPicture(userMeta, json.picture),
      is_admin: json.is_admin === true,
      is_moderator: json.is_moderator === true,
      exp: typeof json.exp === 'number' ? json.exp : 0,
      // VIP claims are top-level only (Custom Access Token Hook); ignore user_metadata.
      vip_is_permanent: json.vip_is_permanent === true,
      vip_expires_at:
        typeof json.vip_expires_at === 'string' ? json.vip_expires_at : null,
    }
  } catch {
    return null
  }
}

/**
 * Derive membership flags from JWT VIP claims.
 * Permanent VIP wins over any expiry; timed VIP requires a future ISO expiry.
 */
export function getVipStatus(
  claims:
    | Pick<AdminClaims, 'vip_is_permanent' | 'vip_expires_at'>
    | null
    | undefined,
  now: number = Date.now(),
): VipStatus {
  if (!claims) {
    return { isPermanentVip: false, isTimedVip: false, isVip: false }
  }
  if (claims.vip_is_permanent === true) {
    return { isPermanentVip: true, isTimedVip: false, isVip: true }
  }
  const expiresAt = claims.vip_expires_at
  if (typeof expiresAt === 'string') {
    const ts = Date.parse(expiresAt)
    if (!Number.isNaN(ts) && ts > now) {
      return { isPermanentVip: false, isTimedVip: true, isVip: true }
    }
  }
  return { isPermanentVip: false, isTimedVip: false, isVip: false }
}

export function isAuthorizedAdmin(claims: AdminClaims | null): boolean {
  if (!claims) return false
  if (!claims.is_admin) return false
  if (!claims.email.toLowerCase().endsWith(ADMIN_EMAIL_DOMAIN)) return false
  if (claims.exp > 0 && claims.exp * 1000 <= Date.now()) return false
  return true
}

let bootstrapped = false

export async function bootstrapAuth(): Promise<void> {
  if (bootstrapped) return
  bootstrapped = true

  const { data } = await supabase.auth.getSession()
  useAuthStore.getState().setSession(data.session)
  useAuthStore.getState().markReady()

  supabase.auth.onAuthStateChange((_event, session) => {
    useAuthStore.getState().setSession(session)
  })
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut()
  useAuthStore.getState().setSession(null)
}
